import {
  read as readXlsxFile,
  utils,
  write as writeXlsx,
  writeFile as writeXlsxFile,
} from 'xlsx';

export const xlsxMIMEType =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const getFileNameFromDisposition = (disposition, fallbackName) => {
  const utf8Match = disposition?.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);

  if (utf8Match?.[1]) {
    const encodedFileName = utf8Match[1].replace(/^"|"$/g, '');

    try {
      return decodeURIComponent(encodedFileName);
    } catch (_error) {
      return encodedFileName;
    }
  }

  const asciiMatch = disposition?.match(/filename\s*=\s*"([^"]+)"/i);
  return asciiMatch?.[1] || fallbackName;
};

export const createBlobFileFromResponse = ({
  response,
  fallbackName = 'Ошибки импорта.xlsx',
}) => {
  const contentType = response?.headers?.['content-type'] || '';
  const disposition = response?.headers?.['content-disposition'] || '';
  const isFile =
    disposition.toLowerCase().includes('attachment') ||
    contentType.includes(xlsxMIMEType) ||
    contentType.includes('application/octet-stream');

  if (!isFile || !response?.data) return undefined;

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType || xlsxMIMEType });

  if (!blob.size) return undefined;

  return {
    blob,
    fileName: getFileNameFromDisposition(disposition, fallbackName),
  };
};

export const downloadBlobFile = ({ blob, fileName }) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const createXlsxBlob = ({ headers }) => {
  const sheet = utils.aoa_to_sheet([headers]);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, sheet);

  const data = writeXlsx(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([data], { type: xlsxMIMEType });
};

const checkIsSheetsEmpty = (workbook) => {
  const { SheetNames = [], Sheets = {} } = workbook || {};
  return !SheetNames.some((sheetName) => Sheets[sheetName]?.['!ref']);
};

const checkIsXlsxMIMEType = (file) => file?.type === xlsxMIMEType;

export const validateAndProcessingXlsx = async ({
  file: rawFile,
  fileList: rawFileList,
}) => {
  let value, errors;

  if (!rawFileList?.length) {
    errors = ['Выберите файл'];
  } else if (!checkIsXlsxMIMEType(rawFile)) {
    errors = ['Неверный формат файла'];
  } else {
    const fileList = rawFileList.slice(-1);
    const { originFileObj } = fileList[0];

    try {
      const arrayBuffer = await originFileObj.arrayBuffer();
      const workbook = readXlsxFile(arrayBuffer, { type: 'array' });

      return checkIsSheetsEmpty(workbook)
        ? { value: undefined, errors: ['Пустой файл'] }
        : { value: fileList, errors: undefined, workbook };
    } catch (error) {
      errors = ['Ошибка при обработке файла'];
    }
  }

  return { value, errors };
};

export const sheetToJSON = ({ worksheet, opts }) =>
  utils.sheet_to_json(worksheet, opts);

const importSheetToJSONBaseOpts = { header: 'A', raw: false };

export const parseImportSheet = ({ worksheet, dataStartRow }) =>
  sheetToJSON({
    worksheet,
    opts: {
      ...importSheetToJSONBaseOpts,
      ...(dataStartRow ? { range: dataStartRow - 1 } : {}),
    },
  });

export const getImportDataRows = ({ sheetData, dataStartRow }) =>
  dataStartRow ? sheetData : sheetData.slice(1);

export const getWorksheetLastRow = (worksheet) => {
  if (!worksheet?.['!ref']) return 0;
  return utils.decode_range(worksheet['!ref']).e.r + 1;
};

const errorPropsNames = ['errors', 'errorMessage', 'errorDescription'];
export const createErrorHeaders = (fields) => {
  const headers = Object.values(fields).reduce((acc, field) => {
    acc.push(field.label);
    return acc;
  }, []);

  headers.push('Ошибки');

  return headers;
};

export const createRows = ({ data, fields, transform = false }) =>
  data.map((item) => {
    const row = Object.values(fields).reduce((acc, field) => {
      const { name, toExcel } = field;

      const itemVal = item[name];
      const value = transform && toExcel ? toExcel(itemVal) : itemVal;
      acc.push(value);
      return acc;
    }, []);

    for (const errName of errorPropsNames) {
      const err = item[errName];
      if (err) {
        row.push(err);
        break;
      }
    }

    return row;
  });

export const createWorkbook = ({ rows, headers }) => {
  const sheet = utils.json_to_sheet(rows);
  utils.sheet_add_aoa(sheet, [headers], { origin: 'A1' });

  const wb = utils.book_new();
  utils.book_append_sheet(wb, sheet);

  return wb;
};

export { readXlsxFile, writeXlsxFile };
